import { isAxiosError } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';

import api, { setAuthToken } from '../api';
import { IPlayerInfo } from '../types/player';
import { IAccount, IUser } from '../types/user';

const STORAGE_KEY = 'user';

const readStoredUser = (): IUser | null => {
    try {
        const user = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        return user?.token ? user : null;
    } catch (_) {
        return null;
    }
};

class Auth {
    user: IUser | null = readStoredUser();
    /** Игрок, к которому привязан аккаунт, — для ссылки на профиль в шапке */
    profile: IPlayerInfo | null = null;

    constructor() {
        makeAutoObservable(this);
        setAuthToken(this.user?.token ?? null);
    }

    async fetchProfile() {
        const playerId = this.user?.player;
        if (!playerId) {
            this.profile = null;
            return;
        }
        try {
            const { data } = await api.get<IPlayerInfo>('/players/' + playerId);
            runInAction(() => this.setProfile(data));
        } catch (err) {
            console.error(err);
        }
    }

    /** Обновляет профиль после правки ника или имени */
    setProfile(player: IPlayerInfo) {
        if (player._id === this.user?.player) this.profile = player;
    }

    get isAuthorized() {
        return !!this.user?.token;
    }

    /** Может управлять игроками и матчами */
    get isAdmin() {
        const role = this.user?.role;
        return role === 'owner' || role === 'admin';
    }

    /** Главный админ: назначает админов */
    get isOwner() {
        return this.user?.role === 'owner';
    }

    /** Вошёл с временным паролем — сначала нужно его сменить */
    get mustChangePassword() {
        return this.isAuthorized && !!this.user?.mustChangePassword;
    }

    /** Бросает ошибку, если логин не удался. */
    async login(login: string, password: string) {
        const { data } = await api.post<IUser>('/auth/login', {
            login,
            password,
        });
        this.setUser(data);
    }

    /** Бросает ошибку, если пароль сменить не удалось. */
    async changePassword(currentPassword: string, newPassword: string) {
        const { data } = await api.post<IUser>('/auth/change-password', {
            currentPassword,
            newPassword,
        });
        this.setUser(data);
    }

    /** Бросает ошибку, если логин сменить не удалось. Токен не меняется. */
    async changeLogin(login: string) {
        const { data } = await api.post<IAccount>('/auth/change-login', {
            login,
        });
        runInAction(() => {
            this.setUser({ ...data, token: this.user?.token });
            if (this.profile) this.profile = { ...this.profile, login };
        });
        return data.login;
    }

    logout() {
        localStorage.removeItem(STORAGE_KEY);
        setAuthToken(null);
        this.user = null;
        this.profile = null;
    }

    /**
     * Проверяет сохранённый токен на сервере и обновляет роль.
     * Протухший токен сбрасывает.
     */
    async verify() {
        const token = this.user?.token;
        if (!token) return;
        try {
            const { data } = await api.get<IAccount>('/auth/me');
            runInAction(() => this.setUser({ ...data, token }));
        } catch (err) {
            // Сетевые ошибки не должны разлогинивать пользователя
            const status = isAxiosError(err) ? err.response?.status : undefined;
            if (status === 401 || status === 403) {
                runInAction(() => this.logout());
            }
        }
    }

    private setUser(user: IUser) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
        setAuthToken(user.token ?? null);
        this.user = user;
        if (this.profile?._id !== user.player) this.fetchProfile();
    }
}

const authStore = new Auth();
export default authStore;
